import React, { useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  message,
  Space,
} from "antd";
import { useQaQuestions } from "./store";
import { isArray, get } from "lodash";

const { Option } = Select;

const QA_AddQuestion = ({
  setIsAdding,
  savedFormValues,
  setTriggerFetch,
  totalScore,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [answerOptions, setAnswerOptions] = useState([
    { text: "", isCorrect: false },
  ]);
  const { fetchAddNewQuestion } = useQaQuestions();

  const handleAddOption = () => {
    setAnswerOptions([...answerOptions, { text: "", isCorrect: false }]);
  };

  const handleRemoveOption = (index) => {
    if (answerOptions.length > 1) {
      const newOptions = answerOptions.filter((_, i) => i !== index);
      setAnswerOptions(newOptions);
    }
  };

  const handleOptionChange = (value, index) => {
    const newOptions = [...answerOptions];
    newOptions[index] = { ...newOptions[index], text: value }; // Update only the text property
    setAnswerOptions(newOptions);
  };

  const handleCorrectToggle = (index) => {
    setAnswerOptions((prevOptions) => {
      const newOptions = [...prevOptions];
      newOptions[index].isCorrect = !newOptions[index].isCorrect; // Toggle the isCorrect status
      return newOptions;
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const newQuestionScore = Number(values?.question_score);

      if (
        (values?.option_type === "checkbox" ||
          values?.option_type === "description") &&
        newQuestionScore > 0
      ) {
        message.error(
          `Score for questions with options type "checkbox" or "description" should be 0. Please check the question score.`
        );
        setLoading(false);
        return;
      }

      // Check if total score would exceed 100
      if (totalScore + newQuestionScore > 100) {
        message.error(
          `Adding this question would exceed the total score limit of 100. Please check question score`
        );
        setLoading(false);
        return;
      }

    // Check if at least one correct answer is selected, only if option_type is not "description"
    if (values?.option_type !== "description") {
      const hasCorrectAnswer = answerOptions.some((option) => option.isCorrect);
      if (!hasCorrectAnswer) {
        message.error("At least one answer option must be marked as correct.");
        setLoading(false);
        return;
      }
    }

      // Prepare correct answers based on the selected options
      const processedCorrectAnswers = answerOptions
        .filter((option) => option.isCorrect)
        .map((option) => option.text);

      const queryParamData = {
        month: isArray(get(savedFormValues, "monthCount"))
          ? 0
          : get(savedFormValues, "monthCount", 0),
        engagement_type: isArray(get(savedFormValues, "engagementType"))
          ? undefined
          : get(savedFormValues, "engagementType"),
      };

      const paramData = {
        monthCount: isArray(get(savedFormValues, "monthCount"))
          ? undefined
          : get(savedFormValues, "monthCount"),
        engagementType: isArray(get(savedFormValues, "engagementType"))
          ? undefined
          : get(savedFormValues, "engagementType"),
      };

      const transformedData = {
        is_critical_question: values?.is_critical_question,
        is_mandatory: values?.is_mandatory,
        is_sub_question: false,
        is_multiple_select_option: values?.option_type === "checkbox",
        main_parameter: values?.main_parameter,
        option_type: values?.option_type,
        parameter_type: values?.parameter_type,
        question_title: values?.question_title,
        question: isArray(values?.question)
          ? values?.question
          : [values?.question],
        answer_options: answerOptions.map((option) => option.text),
        serial_number: Number(values?.serial_number),
        correct_answers: processedCorrectAnswers,
        question_score: newQuestionScore,
        section: values?.section,
        section_name: values?.section_name,
        reference_question_number: values?.reference_question_number,
        is_active: true,
        ...queryParamData,
      };

      const addedData = {
        scoringFormQuestion: transformedData,
        ...paramData,
      };
      fetchAddNewQuestion(addedData);
      setTriggerFetch(true);
      form.resetFields();
      setAnswerOptions([{ value: "", isCorrect: false }]);
      setIsAdding(false);
    } catch (error) {
      message.error("Failed to submit QA Scoring form");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setAnswerOptions([{ text: "", isCorrect: false }]);
    setIsAdding(false);
  };

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  const handleKeyPress = (event) => {
    const { key } = event;
    // Allow digits, a dot, and control keys (backspace, delete, etc.)
    if (
      !/^[0-9.]$/.test(key) &&
      !["Backspace", "Delete", "Tab"].includes(key)
    ) {
      event.preventDefault();
    }
  };

  const handleParse = (value) => {
    // Allow only digits and a single dot
    const cleanedValue = value.replace(/[^0-9.]/g, ""); // Remove invalid characters
    const parts = cleanedValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join(""); // Keep only the first dot
    }
    return cleanedValue;
  };

  return (
    <div>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={{
          option_type: "radio",
          is_mandatory: false, // Default value for is_mandatory
          is_critical_question: false,
        }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              name="question"
              label="Question (Separate multiple questions with commas)"
              rules={[{ required: true, message: "Please input question" }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            {/* Question Title */}
            <Form.Item
              {...formItemLayout}
              name="question_title"
              label="Question Title"
              rules={[
                { required: true, message: "Please input question title" },
              ]}
            >
              <Input type="text" />
            </Form.Item>
            {/* Options Type */}
            <Form.Item
              {...formItemLayout}
              name="option_type"
              label="Options Type"
              rules={[
                { required: true, message: "Please select options type" },
              ]}
            >
              <Select
                onChange={(value) => {
                  if (value === "radio" || value === "checkbox") {
                    setAnswerOptions([""]); // Reset options for radio/checkbox
                  } else {
                    setAnswerOptions([]); // Clear options for description
                  }
                }}
              >
                <Option value="radio">Radio</Option>
                <Option value="checkbox">Checkbox</Option>
                <Option value="description">Description</Option>
              </Select>
            </Form.Item>
            {/* Conditional Rendering for Answer Options */}
            {form.getFieldValue("option_type") !== "description" && (
              <>
                {answerOptions.map((option, index) => (
                  <Form.Item
                    key={index}
                    {...formItemLayout}
                    label={`Answer Option ${index + 1}`}
                    name={["answer_options", index]}
                    rules={[
                      {
                        required: true,
                        message: `Please provide answer option ${index + 1}`,
                      },
                    ]}
                  >
                    <Space>
                      <Input
                        placeholder={`Enter answer option ${index + 1}`}
                        value={option.text} // Access the text property
                        onChange={(e) =>
                          handleOptionChange(e.target.value, index)
                        }
                      />

                      <Switch
                        checked={option.isCorrect}
                        onChange={() => handleCorrectToggle(index)}
                        checkedChildren="Correct"
                        unCheckedChildren={
                          answerOptions.length > 1 ? "Incorrect" : null
                        }
                        style={{
                          display:
                            answerOptions.length > 1 ? "inline-block" : "none",
                        }}
                      />
                      {index === 0 && (
                        <Button
                          type="dashed"
                          onClick={handleAddOption}
                          style={{ marginLeft: 8 }}
                        >
                          +
                        </Button>
                      )}
                      {answerOptions.length > 1 && (
                        <Button
                          danger
                          onClick={() => handleRemoveOption(index)}
                          disabled={answerOptions.length === 1}
                        >
                          Delete
                        </Button>
                      )}
                    </Space>
                  </Form.Item>
                ))}
              </>
            )}
          </Col>
          {/* First Column */}
          <Col span={12}>
            <div>
              {/* Parameter Type */}
              <Form.Item
                {...formItemLayout}
                name="parameter_type"
                label="Parameter Type"
                rules={[
                  { required: true, message: "Please select parameter type" },
                ]}
              >
                <Input type="text" />
              </Form.Item>

              {/* Main Parameter */}
              <Form.Item
                {...formItemLayout}
                name="main_parameter"
                label="Main Parameter"
                rules={[
                  { required: true, message: "Please input main parameter" },
                ]}
              >
                <Input type="text" />
              </Form.Item>

              {/* Section Name */}
              <Form.Item
                {...formItemLayout}
                name="section_name"
                label="Section Name"
                rules={[
                  { required: true, message: "Please input section name" },
                ]}
              >
                <Input type="text" />
              </Form.Item>

              <Form.Item
                {...formItemLayout}
                name="section"
                label="Section"
                rules={[{ required: true, message: "Please input section" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  parser={handleParse}
                  onKeyPress={handleKeyPress}
                  formatter={(value) => value}
                />
              </Form.Item>
              <Form.Item
                {...formItemLayout}
                name="serial_number"
                label="Serial Number"
                rules={[
                  { required: true, message: "Please input Serial Number" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  parser={handleParse}
                  onKeyPress={handleKeyPress}
                  formatter={(value) => value}
                />
              </Form.Item>
            </div>
          </Col>

          {/* Second Column */}
          <Col span={12}>
            <div>
              {/* Question Score */}
              <Form.Item
                {...formItemLayout}
                name="question_score"
                label="Question Score"
                rules={[
                  { required: true, message: "Please input question score" },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  parser={handleParse}
                  onKeyPress={handleKeyPress}
                  formatter={(value) => value}
                />
              </Form.Item>
              <Form.Item
                {...formItemLayout}
                name="reference_question_number"
                label="Reference Question Number"
                rules={[
                  {
                    required: true,
                    message: "Please Reference Question Number",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  parser={handleParse}
                  onKeyPress={handleKeyPress}
                  formatter={(value) => value}
                />
              </Form.Item>
              {/* Toggle Fields */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    {...formItemLayout}
                    name="is_mandatory"
                    label="Is Mandatory?"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    {...formItemLayout}
                    name="is_critical_question"
                    label="Is Critical Question?"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Submit and Clear Buttons */}
        <Row style={{ marginTop: 20 }} gutter={16}>
          <Col span={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: "100%" }}
            >
              Submit
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="default"
              onClick={handleClear}
              style={{ width: "100%" }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default QA_AddQuestion;
